import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

/* ---- Storage helpers (so we don't pester users who said "Not now") ---- */
const DISMISS_KEY = "te_pwa_dismissed_at";
const COOLDOWN_DAYS = 14;

function recentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY));
    if (!ts) return false;
    return Date.now() - ts < COOLDOWN_DAYS * 86400 * 1000;
  } catch { return false; }
}
function rememberDismiss() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPadOS reports as Macintosh; double-check for touch.
  const isIPadOS = ua.includes("Mac") && "ontouchend" in document;
  return isIOSDevice || isIPadOS;
}

/**
 * InstallPrompt — shows a small bottom banner inside the phone-frame.
 * • Android/Desktop Chrome: triggers the native beforeinstallprompt flow.
 * • iOS Safari: shows manual "Share → Add to Home Screen" instructions.
 * Hidden after install, in standalone mode, or after user dismissal.
 */
export function InstallPrompt() {
  const { language } = useI18n();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  const L = (ar, he, en) => language === "ar" ? ar : language === "he" ? he : en;

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    const onInstalled = () => {
      setShow(false);
      rememberDismiss();
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS doesn't fire beforeinstallprompt → show a manual hint after a delay
    if (isIOS()) {
      const tid = setTimeout(() => setShow(true), 4000);
      return () => {
        clearTimeout(tid);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!show) return null;

  const dismiss = () => { rememberDismiss(); setShow(false); setIosHint(false); };

  const install = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted" || outcome === "dismissed") {
          rememberDismiss();
          setShow(false);
          setDeferred(null);
        }
      } catch {
        rememberDismiss(); setShow(false);
      }
    } else if (isIOS()) {
      setIosHint(true);
    }
  };

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed left-1/2 -translate-x-1/2 z-[60] px-3 w-full max-w-md pointer-events-none"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 5rem)" }}
    >
      <div
        className="pointer-events-auto te-card te-fade-up flex items-center gap-3 p-3 shadow-[0_10px_30px_-10px_rgba(103,58,183,0.35)] border-[var(--te-primary)]/30"
        role="dialog"
        aria-live="polite"
      >
        <div className="w-10 h-10 rounded-xl bg-[var(--te-primary-soft)] flex items-center justify-center shrink-0">
          <Smartphone size={20} className="text-[var(--te-primary)]" />
        </div>
        <div className="flex-1 min-w-0">
          {!iosHint ? (
            <>
              <div className="font-bold text-[14px] text-[var(--te-text)] truncate" dir="auto">
                {L("ثبّت TownEye على هاتفك", "התקן את TownEye בטלפון", "Install TownEye on your phone")}
              </div>
              <div className="text-[12px] text-[var(--te-text-secondary)] truncate" dir="auto">
                {L("استخدم التطبيق دون متصفّح", "השתמש כאפליקציה ללא דפדפן", "Use it like a real app, no browser bar")}
              </div>
            </>
          ) : (
            <div className="text-[12px] text-[var(--te-text-secondary)] leading-snug" dir="auto">
              {L(
                "افتح قائمة المشاركة في Safari ثم اختر «إضافة إلى الشاشة الرئيسية».",
                "פתח את תפריט השיתוף בספארי ובחר ״הוסף למסך הבית״.",
                "Open Safari's Share menu and choose “Add to Home Screen”."
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!iosHint && (
            <button
              type="button"
              onClick={install}
              data-testid="pwa-install-btn"
              className="te-btn-primary !py-1.5 !px-3 text-[12px]"
            >
              <Download size={13} /> {L("ثبّت", "התקן", "Install")}
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            data-testid="pwa-dismiss-btn"
            aria-label={L("إغلاق", "סגור", "Dismiss")}
            className="w-8 h-8 rounded-full hover:bg-[var(--te-bg-soft)] flex items-center justify-center text-[var(--te-text-muted)]"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
